import { Directive, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { DateTime } from 'luxon';

@Directive({
  selector: '[rmsInputMask]',
})
export class InputMaskDirective implements OnInit, OnDestroy, ControlValueAccessor {
  private ngControl = inject(NgControl, { self: true });
  private elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private renderer = inject(Renderer2);

  @Input() public mask: (value: string) => string = (value: string) => value;
  @Input() public unmask: (value: string) => string | number | boolean | DateTime | null = (value: string) => value;

  private readonly rendererListeners: (() => void)[] = [];

  private onChange?: (_: string | number | boolean | DateTime | null) => void;
  private onTouched?: () => void;

  constructor() {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    const el = this.elementRef.nativeElement as HTMLInputElement;

    /* set mask value on input init */
    if (el.value && this.mask) {
      el.value = this.mask(el.value);
    }

    this.rendererListeners.push(
      this.renderer.listen(el, 'input', (event: Event) => {
        const target = event.target as HTMLInputElement;

        /* mask values for typing */
        if (this.mask) {
          target.value = this.mask(target.value);
        }

        /* if we have unmask - set it to form value  */
        const value = this.unmask ? this.unmask(target.value) : target.value;
        this.onChange?.(value);
      }),
      this.renderer.listen(el, 'focusout', () => {
        this.onTouched?.();
      })
    );
  }

  public writeValue(value: string): void {
    const maskedValue = this.mask ? this.mask(value) : value;

    this.elementRef.nativeElement.value = maskedValue ?? '';
  }

  public registerOnChange(fn: (value: string | number | boolean | DateTime | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnDestroy(): void {
    this.rendererListeners.forEach((listener) => listener());
  }
}
